import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Button, Modal, Form, Tab, Tabs } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#0dcaf0", color: "#000" },
    IN_PROGRESS: { backgroundColor: "#ffc107", color: "#000" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    SKIPPED: { backgroundColor: "#6c757d", color: "#fff" },
    OVERDUE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.PENDING;
};

const getTaskTypeStyle = (type) => {
  const styles = {
    CLEANING: { backgroundColor: "#0dcaf0", color: "#000" },
    INSPECTION: { backgroundColor: "#0d6efd", color: "#fff" },
    MAINTENANCE: { backgroundColor: "#ffc107", color: "#000" },
    SANITIZATION: { backgroundColor: "#198754", color: "#fff" },
    SAFETY_CHECK: { backgroundColor: "#ff5454", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[type] || styles.OTHER;
};

const StatCard = ({ title, value, icon, variant = "primary", alert = false }) => {
  const colors = {
    primary: "#0d6efd",
    success: "#198754",
    warning: "#ffc107",
    danger: "#ff5454",
    info: "#0dcaf0",
    secondary: "#6c757d",
  };
  const color = colors[variant] || colors.primary;

  return (
    <Card className="h-100">
      <Card.Body className="p-3 text-center">
        <div className="mb-2">
          <i className={`fa ${icon} fa-2x`} style={{ color, opacity: 0.8 }}></i>
        </div>
        <h3 className="mb-1" style={{ color }}>
          {value}
          {alert && <i className="fa fa-exclamation-triangle ms-2" style={{ fontSize: "0.6em" }}></i>}
        </h3>
        <small className="text-muted">{title}</small>
      </Card.Body>
    </Card>
  );
};

const TaskCard = ({ task, onStart, onComplete, onSkip, onAddPhoto }) => {
  const isOverdue = task.is_overdue || task.status === "OVERDUE";

  return (
    <Card className={`mb-2 ${isOverdue ? "border-danger" : ""}`}>
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-1">
              <strong>{task.task_name}</strong>
              {task.requires_photo && (
                <i
                  className="fa fa-camera ms-2"
                  style={{ color: "#6c757d" }}
                  title="Photo required"
                ></i>
              )}
            </div>
            <div className="small text-muted mb-1">
              <i className={`fa ${task.target_type === "MACHINE" ? "fa-cog" : "fa-building"} me-1`}></i>
              {task.target_name}
              <span className="mx-1">|</span>
              <i className="fa fa-map-marker me-1"></i>
              {task.location_name}
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge badge-pill" style={getTaskTypeStyle(task.task_type)}>
                {task.task_type_display || task.task_type}
              </span>
              <span className="badge badge-pill" style={getStatusStyle(task.status)}>
                {task.status_display || task.status}
              </span>
              {task.due_time && (
                <small className="text-muted">
                  <i className="fa fa-clock-o me-1"></i>
                  {task.due_time.substring(0, 5)}
                </small>
              )}
              {task.estimated_duration_minutes > 0 && (
                <small className="text-muted">
                  <i className="fa fa-hourglass-half me-1"></i>
                  {task.estimated_duration_minutes} min
                </small>
              )}
              {isOverdue && (
                <small style={{ color: "#ff5454" }}>
                  <i className="fa fa-exclamation-triangle me-1"></i>
                  Overdue
                </small>
              )}
            </div>
          </div>
          <div className="d-flex gap-1">
            {task.status === "PENDING" && (
              <Button
                variant="warning"
                size="sm"
                onClick={() => onStart(task.id)}
                title="Start working on this task"
              >
                <i className="fa fa-play"></i>
                <span className="ms-1 d-none d-md-inline">Start</span>
              </Button>
            )}
            {task.status === "IN_PROGRESS" && (
              <>
                {task.requires_photo && (!task.photos || task.photos.length === 0) && (
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => onAddPhoto(task)}
                    title="Upload a photo (required before completing)"
                  >
                    <i className="fa fa-camera"></i>
                  </Button>
                )}
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => onComplete(task)}
                  title={task.requires_photo && (!task.photos || task.photos.length === 0)
                    ? "Photo required before completing"
                    : "Mark this task as completed"}
                  disabled={task.requires_photo && (!task.photos || task.photos.length === 0)}
                >
                  <i className="fa fa-check"></i>
                  <span className="ms-1 d-none d-md-inline">Done</span>
                </Button>
              </>
            )}
            {(task.status === "PENDING" || task.status === "IN_PROGRESS") && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onSkip(task)}
                title="Skip this task (requires a reason)"
              >
                <i className="fa fa-forward"></i>
                <span className="ms-1 d-none d-md-inline">Skip</span>
              </Button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const ScheduledTasksDashboard = ({
  dashboardStats,
  todayTasks,
  overdueTasks,
  upcomingTasks,
  loading,
  error,
  onStartTask,
  onCompleteTask,
  onSkipTask,
  onAddPhoto,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState("today");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [skipReason, setSkipReason] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCaption, setPhotoCaption] = useState("");

  const handleStartTask = (taskId) => {
    if (onStartTask) {
      onStartTask(taskId);
    }
  };

  const handleOpenComplete = (task) => {
    setSelectedTask(task);
    setCompletionNotes("");
    setShowCompleteModal(true);
  };

  const handleCompleteTask = async () => {
    if (onCompleteTask && selectedTask) {
      try {
        await onCompleteTask(selectedTask.id, { completion_notes: completionNotes });
      } catch (err) {
        console.error("Error completing task:", err);
      }
    }
    setShowCompleteModal(false);
    setSelectedTask(null);
    setCompletionNotes("");
  };

  const handleOpenSkip = (task) => {
    setSelectedTask(task);
    setSkipReason("");
    setShowSkipModal(true);
  };

  const handleSkipTask = async () => {
    if (onSkipTask && selectedTask && skipReason.trim()) {
      try {
        await onSkipTask(selectedTask.id, { skip_reason: skipReason });
      } catch (err) {
        console.error("Error skipping task:", err);
      }
    }
    setShowSkipModal(false);
    setSelectedTask(null);
    setSkipReason("");
  };

  const handleOpenPhoto = (task) => {
    setSelectedTask(task);
    setPhotoFile(null);
    setPhotoCaption("");
    setShowPhotoModal(true);
  };

  const handleAddPhoto = () => {
    if (onAddPhoto && selectedTask && photoFile) {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("caption", photoCaption);
      onAddPhoto(selectedTask.id, formData);
    }
    setShowPhotoModal(false);
    setSelectedTask(null);
  };

  // Group today's tasks by status
  const pendingTasks = todayTasks.filter((t) => t.status === "PENDING");
  const inProgressTasks = todayTasks.filter((t) => t.status === "IN_PROGRESS");
  const completedTasks = todayTasks.filter((t) => t.status === "COMPLETED" || t.status === "SKIPPED");

  // Group upcoming tasks by date
  const upcomingByDate = upcomingTasks.reduce((acc, task) => {
    const date = task.scheduled_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(task);
    return acc;
  }, {});

  if (loading && !dashboardStats) {
    return <Spinner />;
  }

  return (
    <div className="scheduled-tasks-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="fa fa-tasks me-2"></i>
          Task Dashboard
        </h4>
        <div>
          <Button variant="outline-secondary" size="sm" onClick={onRefresh} className="me-2">
            <i className="fa fa-refresh me-1"></i> Refresh
          </Button>
          <Link to="/maintenance/task-templates" className="btn btn-outline-primary btn-sm me-2">
            <i className="fa fa-file-text-o me-1"></i> Templates
          </Link>
          <Link to="/maintenance/task-assignments" className="btn btn-primary btn-sm">
            <i className="fa fa-link me-1"></i> Assignments
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading tasks"}
        </div>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Pending"
            value={dashboardStats?.total_pending || 0}
            icon="fa-clock-o"
            variant="info"
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Overdue"
            value={dashboardStats?.total_overdue || 0}
            icon="fa-exclamation-triangle"
            variant="danger"
            alert={dashboardStats?.total_overdue > 0}
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Completed Today"
            value={dashboardStats?.completed_today || 0}
            icon="fa-check-circle"
            variant="success"
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="This Week"
            value={dashboardStats?.upcoming_this_week || 0}
            icon="fa-calendar"
            variant="primary"
          />
        </Col>
      </Row>

      {/* Task Tabs */}
      <Card>
        <Card.Header className="reports-filter p-0">
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="pt-2 px-2">
            <Tab
              eventKey="today"
              title={
                <>
                  <i className="fa fa-calendar-check-o me-1"></i>
                  Today ({todayTasks.length})
                </>
              }
            />
            <Tab
              eventKey="overdue"
              title={
                <>
                  <i className="fa fa-exclamation-triangle me-1" style={{ color: overdueTasks.length > 0 ? "#ff5454" : "inherit" }}></i>
                  Overdue ({overdueTasks.length})
                </>
              }
            />
            <Tab
              eventKey="upcoming"
              title={
                <>
                  <i className="fa fa-calendar me-1"></i>
                  Upcoming ({upcomingTasks.length})
                </>
              }
            />
          </Tabs>
        </Card.Header>
        <Card.Body className="p-3" style={{ maxHeight: "500px", overflowY: "auto" }}>
          {activeTab === "today" && (
            <>
              {todayTasks.length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-check-circle fa-3x mb-3" style={{ color: "#198754" }}></i>
                  <p className="mb-0 text-muted">No tasks scheduled for today</p>
                </div>
              ) : (
                <>
                  {inProgressTasks.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fa fa-spinner me-1"></i> In Progress ({inProgressTasks.length})
                      </h6>
                      {inProgressTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStart={handleStartTask}
                          onComplete={handleOpenComplete}
                          onSkip={handleOpenSkip}
                          onAddPhoto={handleOpenPhoto}
                        />
                      ))}
                    </div>
                  )}
                  {pendingTasks.length > 0 && (
                    <div className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fa fa-clock-o me-1"></i> Pending ({pendingTasks.length})
                      </h6>
                      {pendingTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStart={handleStartTask}
                          onComplete={handleOpenComplete}
                          onSkip={handleOpenSkip}
                          onAddPhoto={handleOpenPhoto}
                        />
                      ))}
                    </div>
                  )}
                  {completedTasks.length > 0 && (
                    <div>
                      <h6 className="text-muted mb-2">
                        <i className="fa fa-check me-1"></i> Completed/Skipped ({completedTasks.length})
                      </h6>
                      {completedTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStart={handleStartTask}
                          onComplete={handleOpenComplete}
                          onSkip={handleOpenSkip}
                          onAddPhoto={handleOpenPhoto}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === "overdue" && (
            <>
              {overdueTasks.length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-check-circle fa-3x mb-3" style={{ color: "#198754" }}></i>
                  <p className="mb-0 text-muted">No overdue tasks</p>
                </div>
              ) : (
                overdueTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={{ ...task, is_overdue: true }}
                    onStart={handleStartTask}
                    onComplete={handleOpenComplete}
                    onSkip={handleOpenSkip}
                    onAddPhoto={handleOpenPhoto}
                  />
                ))
              )}
            </>
          )}

          {activeTab === "upcoming" && (
            <>
              {upcomingTasks.length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-calendar-o fa-3x mb-3 text-muted"></i>
                  <p className="mb-0 text-muted">No upcoming tasks</p>
                </div>
              ) : (
                Object.keys(upcomingByDate)
                  .sort()
                  .map((date) => (
                    <div key={date} className="mb-3">
                      <h6 className="text-muted mb-2">
                        <i className="fa fa-calendar me-1"></i>
                        {moment(date).format("dddd, MMM D")}
                        <span className="badge badge-pill ms-2" style={{ backgroundColor: "#6c757d" }}>
                          {upcomingByDate[date].length}
                        </span>
                      </h6>
                      {upcomingByDate[date].map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onStart={handleStartTask}
                          onComplete={handleOpenComplete}
                          onSkip={handleOpenSkip}
                          onAddPhoto={handleOpenPhoto}
                        />
                      ))}
                    </div>
                  ))
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Complete Task Modal */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} dialogClassName="modal-logut" animation={false}>
        <Card>
          <Card.Header className="reports-filter">
            <i className="fa fa-check-circle me-2"></i>
            Complete Task
          </Card.Header>
          <Card.Body>
            {selectedTask && (
              <>
                <p className="mb-2">
                  <strong>{selectedTask.task_name}</strong>
                </p>
                <p className="text-muted small mb-3">
                  {selectedTask.target_name} - {selectedTask.location_name}
                </p>
                <Form.Group>
                  <Form.Label className="small">Completion Notes (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    size="sm"
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    placeholder="Add any notes about the completed task..."
                  />
                </Form.Group>
              </>
            )}
          </Card.Body>
          <Card.Footer className="d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowCompleteModal(false)}>
              Cancel
            </Button>
            <Button variant="success" size="sm" onClick={handleCompleteTask}>
              <i className="fa fa-check me-1"></i> Complete
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      {/* Skip Task Modal */}
      <Modal show={showSkipModal} onHide={() => setShowSkipModal(false)} dialogClassName="modal-logut" animation={false}>
        <Card>
          <Card.Header className="reports-filter">
            <i className="fa fa-forward me-2"></i>
            Skip Task
          </Card.Header>
          <Card.Body>
            {selectedTask && (
              <>
                <p className="mb-2">
                  <strong>{selectedTask.task_name}</strong>
                </p>
                <p className="text-muted small mb-3">
                  {selectedTask.target_name} - {selectedTask.location_name}
                </p>
                <Form.Group>
                  <Form.Label className="small">Reason for skipping *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    size="sm"
                    value={skipReason}
                    onChange={(e) => setSkipReason(e.target.value)}
                    placeholder="Please provide a reason for skipping this task..."
                    required
                  />
                </Form.Group>
              </>
            )}
          </Card.Body>
          <Card.Footer className="d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowSkipModal(false)}>
              Cancel
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={handleSkipTask}
              disabled={!skipReason.trim()}
            >
              <i className="fa fa-forward me-1"></i> Skip Task
            </Button>
          </Card.Footer>
        </Card>
      </Modal>

      {/* Add Photo Modal */}
      <Modal show={showPhotoModal} onHide={() => setShowPhotoModal(false)} dialogClassName="modal-logut" animation={false}>
        <Card>
          <Card.Header className="reports-filter">
            <i className="fa fa-camera me-2"></i>
            Add Photo
          </Card.Header>
          <Card.Body>
            {selectedTask && (
              <>
                <p className="mb-2">
                  <strong>{selectedTask.task_name}</strong>
                </p>
                <p className="text-muted small mb-3">
                  Photo is required before completing this task.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label className="small">Photo *</Form.Label>
                  <Form.Control
                    type="file"
                    size="sm"
                    accept="image/*"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="small">Caption (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={photoCaption}
                    onChange={(e) => setPhotoCaption(e.target.value)}
                    placeholder="Add a caption..."
                  />
                </Form.Group>
              </>
            )}
          </Card.Body>
          <Card.Footer className="d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowPhotoModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleAddPhoto} disabled={!photoFile}>
              <i className="fa fa-upload me-1"></i> Upload Photo
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </div>
  );
};

export default ScheduledTasksDashboard;
